import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Loader2, LogOut, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NitroReportForm from './NitroReportForm';
import FreshClinicsReportFormV1 from './FreshClinicsReportFormV1';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportGenerator = ({ onLogout }) => {
  const navigate = useNavigate();
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('nitro-valuation');
  const [month, setMonth] = useState('September');
  const [year, setYear] = useState('2025');
  const [attributionFile, setAttributionFile] = useState(null);
  const [holdingsFile, setHoldingsFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReportTypes, setLoadingReportTypes] = useState(true);
  const [reportTypesError, setReportTypesError] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 10 }, (_, i) => (2030 - i).toString());

  useEffect(() => {
    fetchReportTypes();
  }, []);

  const fetchReportTypes = async (retryCount = 0) => {
    setLoadingReportTypes(true);
    setReportTypesError(false);
    try {
      const response = await axios.get(`${API}/report-types`, {
        timeout: 10000, // 10 second timeout
      });
      setReportTypes(response.data.report_types);
      setLoadingReportTypes(false);
    } catch (error) {
      console.error('Error fetching report types:', error);
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Max 5 seconds
        console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => {
          fetchReportTypes(retryCount + 1);
        }, delay);
      } else {
        setLoadingReportTypes(false);
        setReportTypesError(true);
        toast.error('Failed to load report types. Please try again.');
      }
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'attribution') {
        setAttributionFile(file);
      } else {
        setHoldingsFile(file);
      }
    }
  };

  const handleGenerate = async () => {
    if (!attributionFile) {
      toast.error('Please upload the Attribution file');
      return;
    }

    setLoading(true);
    toast.info('Processing report with AI... This may take 1-2 minutes.', {
      duration: 5000,
    });
    try {
      const formData = new FormData();
      formData.append('attribution_file', attributionFile);
      if (holdingsFile) {
        formData.append('holdings_file', holdingsFile);
      }
      formData.append('report_type', selectedReportType);
      formData.append('month', month);
      formData.append('year', year);

      const response = await axios.post(`${API}/generate-report`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180000, // 3 minutes timeout for AI processing
      });

      if (response.data.success) {
        toast.success('Report generated successfully!');
        navigate(`/report/${response.data.report_id}`);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">

      {/* Sticky top navbar */}
      {onLogout && (
        <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200 shadow-sm">
          <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center justify-end gap-2">
            <Button
              onClick={() => navigate('/amazon-q')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="amazon-q-button"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Amazon Q</span>
              <span className="sm:hidden">Q</span>
            </Button>
            <Button
              onClick={() => navigate('/saved-reports')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">View Saved Reports</span>
              <span className="sm:hidden">Reports</span>
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="logout-button"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        {/* Centered header */}
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Report Generator
          </h1>
          <p className="text-slate-600 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            Proof of Concept (POC) for Ellerston Capital
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Report Type Selection - Always Visible */}
          <Card className="shadow-xl border-0 mb-6">
            <CardHeader className="rounded-t-lg" style={{ backgroundColor: '#A8A67A' }}>
              <CardTitle className="text-2xl text-white">Report Configuration</CardTitle>
              <CardDescription className="text-white opacity-90">
                Select report type and configure parameters
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="report-type" className="text-base font-semibold mb-2 block">
                    Report Type
                  </Label>
                  
                  {loadingReportTypes ? (
                    <div className="flex items-center justify-center h-12 border rounded-md bg-slate-50">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-600 mr-2" />
                      <span className="text-sm text-slate-600">Loading report types...</span>
                    </div>
                  ) : reportTypesError ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center h-12 border rounded-md bg-red-50 border-red-300">
                        <span className="text-sm text-red-600">Failed to load report types</span>
                      </div>
                      <Button 
                        onClick={() => fetchReportTypes(0)} 
                        variant="outline" 
                        className="w-full"
                      >
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                      <SelectTrigger id="report-type" data-testid="report-type-select" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {reportTypes.map((type) => (
                          <SelectItem
                            key={type.id}
                            value={type.id}
                            disabled={type.status !== 'active'}
                          >
                            {type.name} {type.status !== 'active' && '(Coming Soon)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="month" className="text-base font-semibold mb-2 block">
                          Month
                        </Label>
                        <Select value={month} onValueChange={setMonth}>
                          <SelectTrigger id="month" data-testid="month-select" className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((m) => (
                              <SelectItem key={m} value={m}>
                                {m}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="year" className="text-base font-semibold mb-2 block">
                          Year
                        </Label>
                        <Select value={year} onValueChange={setYear}>
                          <SelectTrigger id="year" data-testid="year-select" className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {years.map((y) => (
                              <SelectItem key={y} value={y}>
                                {y}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                </>

              </div>
            </CardContent>
          </Card>

          {/* Report-Specific Forms */}
          {selectedReportType === 'nitro-valuation' ? (
            <>
              {/* Manage Report Button */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-900">Manage Report Structure</h3>
                    <p className="text-sm text-blue-700">Configure sections, edit prompts, and upload examples from previous reports</p>
                  </div>
                  <Button
                    onClick={() => navigate('/manage-nitro')}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    Manage Report
                  </Button>
                </div>
              </div>
              
              <NitroReportForm 
                onSubmit={(reportId) => navigate(`/nitro-report/${reportId}`)} 
                loading={loading}
                quarter={String(Math.ceil((['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(month) + 1) / 3))}
                year={year}
                month={month}
              />
            </>
          ) : selectedReportType === 'fresh-clinics-valuation-v1' ? (
            <>
              {/* Manage Report Button */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-900">Manage Report Structure – Fresh Clinics (v1)</h3>
                    <p className="text-sm text-blue-700">Configure sections, edit prompts, and upload examples from previous reports</p>
                  </div>
                  <Button
                    onClick={() => navigate('/manage-fresh-clinics-v1')}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    Manage Report
                  </Button>
                </div>
              </div>
              
              <FreshClinicsReportFormV1 
                onSubmit={(reportId) => navigate(`/fresh-clinics-report-v1/${reportId}`)} 
                loading={loading}
                quarter={String(Math.ceil((['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(month) + 1) / 3))}
                year={year}
                month={month}
              />
            </>
          ) : (
            <Card className="shadow-xl border-0" data-testid="report-generator-card">
            <CardHeader className="rounded-t-lg" style={{ backgroundColor: '#A8A67A' }}>
              <CardTitle className="text-2xl text-white">Excel File Upload</CardTitle>
              <CardDescription className="text-white opacity-90">
                Upload your Excel files for Australian Emerging Leaders Fund report
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-slate-400 transition-colors">
                    <Label htmlFor="attribution-file" className="cursor-pointer">
                      <div className="flex flex-col items-center space-y-3">
                        <FileSpreadsheet className="w-12 h-12 text-slate-600" />
                        <div className="text-center">
                          <p className="text-base font-semibold text-slate-700">
                            Attribution File (Required)
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            {attributionFile ? attributionFile.name : 'Click to upload Excel file'}
                          </p>
                        </div>
                      </div>
                      <input
                        id="attribution-file"
                        data-testid="attribution-file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => handleFileChange(e, 'attribution')}
                        className="hidden"
                      />
                    </Label>
                  </div>

                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 hover:border-slate-300 transition-colors">
                    <Label htmlFor="holdings-file" className="cursor-pointer">
                      <div className="flex flex-col items-center space-y-3">
                        <FileSpreadsheet className="w-10 h-10 text-slate-400" />
                        <div className="text-center">
                          <p className="text-base font-semibold text-slate-600">
                            Holdings/Exposure File (Optional)
                          </p>
                          <p className="text-sm text-slate-400 mt-1">
                            {holdingsFile ? holdingsFile.name : 'Click to upload Excel file'}
                          </p>
                        </div>
                      </div>
                      <input
                        id="holdings-file"
                        data-testid="holdings-file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => handleFileChange(e, 'holdings')}
                        className="hidden"
                      />
                    </Label>
                  </div>
                </div>

                <Button
                  data-testid="generate-report-btn"
                  onClick={handleGenerate}
                  disabled={loading || !attributionFile}
                  className="w-full h-14 text-lg font-semibold"
                  style={{ backgroundColor: '#A8A67A' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#98966a'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#A8A67A'}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-5 w-5" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;